<template>
  <div class="si-page">
    <div class="si-toolbar">
      <div class="si-toolbar__inner">
        <v-select
          v-model="partnerId"
          :items="partners"
          item-text="name"
          item-value="id"
          label="Partner"
          outlined
          dense
          hide-details
          :disabled="loading"
          class="si-field"
          @change="loadInvoices"
        />

        <v-text-field
          v-model="search"
          placeholder="Filter invoices"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
          class="si-search"
        />

        <v-spacer />

        <div v-if="invoices.length" class="si-summary">
          <span class="si-summary__item">
            <strong>{{ money(totals.net) }}</strong> invoiced
          </span>
          <span class="si-summary__item si-summary__item--muted">
            <strong>{{ money(totals.settled) }}</strong> settled
          </span>
        </div>
      </div>
    </div>

    <div v-if="loading" class="si-state">
      <v-progress-circular indeterminate color="primary" size="40" width="3" />
      <p class="si-state__text">Loading service invoices…</p>
    </div>

    <v-card v-else-if="!partnerId" outlined class="si-state si-state--card">
      <v-icon size="44" color="grey lighten-1">mdi-file-document-multiple-outline</v-icon>
      <h3 class="si-state__title">Pick a partner</h3>
      <p class="si-state__text">
        Every service invoice raised for the partner, and what has been paid
        against it.
      </p>
    </v-card>

    <v-card v-else-if="!invoices.length" outlined class="si-state si-state--card">
      <v-icon size="44" color="grey lighten-1">mdi-file-outline</v-icon>
      <h3 class="si-state__title">No invoices raised yet</h3>
      <p class="si-state__text">
        Work out a period on the Invoice Working screen and raise it from
        there.
      </p>
    </v-card>

    <v-card v-else outlined class="si-card">
      <v-data-table
        :headers="headers"
        :items="invoices"
        :search="search"
        :footer-props="{ 'items-per-page-options': [25, 50, -1] }"
        dense
        class="si-table"
        @click:row="openInvoice"
      >
        <template #[`item.period`]="{ item }">
          <span class="si-period">
            {{ date(item.from_date) }} – {{ date(item.to_date) }}
          </span>
          <span class="si-sub">{{ batchLabel(item) }}</span>
        </template>

        <template #[`item.invoice_number`]="{ item }">
          {{ item.invoice_number || "—" }}
        </template>

        <template #[`item.collection_amount`]="{ item }">
          {{ money(item.collection_amount) }}
          <span class="si-sub">{{ item.collection_count }} collections</span>
        </template>

        <template #[`item.gross_amount`]="{ item }">
          {{ money(item.gross_amount) }}
        </template>

        <template #[`item.gst_amount`]="{ item }">
          {{ money(item.gst_amount) }}
        </template>

        <template #[`item.net_amount`]="{ item }">
          <strong>{{ money(item.net_amount) }}</strong>
        </template>

        <template #[`item.status`]="{ item }">
          <v-chip x-small label :color="statusColor(item.status)" text-color="white">
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn icon x-small @click.stop="openInvoice(item)">
            <v-icon small>mdi-eye-outline</v-icon>
          </v-btn>
          <v-btn
            v-if="item.status !== 'settled'"
            icon
            x-small
            @click.stop="openSettlement(item)"
          >
            <v-icon small>mdi-bank-transfer-in</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="detailDialog" max-width="720px" scrollable>
      <v-card v-if="selected">
        <v-card-title class="font-weight-medium secondary--text primary py-3">
          <span>Invoice {{ selected.invoice_number || "(unnumbered)" }}</span>
          <v-spacer />
          <v-btn icon x-small @click="detailDialog = false">
            <v-icon x-small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <div class="si-meta">
            <div class="si-meta__row">
              <span>Period</span>
              <strong>
                {{ date(selected.from_date) }} – {{ date(selected.to_date) }}
              </strong>
            </div>
            <div class="si-meta__row">
              <span>Batch</span><strong>{{ batchLabel(selected) }}</strong>
            </div>
            <div class="si-meta__row">
              <span>Collections</span>
              <strong>
                {{ selected.collection_count }} ·
                {{ money(selected.collection_amount) }}
              </strong>
            </div>
          </div>

          <table class="si-lines">
            <thead>
              <tr>
                <th>Service Category</th>
                <th class="si-num">Base</th>
                <th class="si-num">Rate</th>
                <th class="si-num">Gross</th>
                <th class="si-num">GST</th>
                <th class="si-num">Net</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in selected.category_lines" :key="line.key">
                <td>
                  {{ line.name }}
                  <span class="si-sub">HSN {{ line.hsn }}</span>
                </td>
                <td class="si-num">{{ money(line.base_amount) }}</td>
                <td class="si-num">{{ line.rate }}%</td>
                <td class="si-num">{{ money(line.gross_amount) }}</td>
                <td class="si-num">{{ money(line.gst_amount) }}</td>
                <td class="si-num">{{ money(line.net_amount) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="3">Total</td>
                <td class="si-num">{{ money(selected.gross_amount) }}</td>
                <td class="si-num">{{ money(selected.gst_amount) }}</td>
                <td class="si-num">{{ money(selected.net_amount) }}</td>
              </tr>
            </tfoot>
          </table>

          <div v-if="selected.utr_number || selected.transaction_id" class="si-settlement">
            <h4 class="si-settlement__title">Settlement</h4>
            <div class="si-meta__row">
              <span>UTR</span><strong>{{ selected.utr_number || "—" }}</strong>
            </div>
            <div class="si-meta__row">
              <span>Transaction</span>
              <strong>{{ selected.transaction_id || "—" }}</strong>
            </div>
            <div class="si-meta__row">
              <span>Paid On</span>
              <strong>{{ date(selected.transaction_date) }}</strong>
            </div>
            <div class="si-meta__row">
              <span>Bank</span><strong>{{ selected.bank_name || "—" }}</strong>
            </div>
            <div v-if="selected.remarks" class="si-meta__row">
              <span>Remarks</span><strong>{{ selected.remarks }}</strong>
            </div>
          </div>
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn text small @click="detailDialog = false">Close</v-btn>
          <v-btn
            v-if="selected.status !== 'settled'"
            small
            depressed
            color="primary"
            @click="openSettlement(selected)"
          >
            Record Settlement
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="settlementDialog" max-width="560px" persistent scrollable>
      <v-card v-if="selected">
        <v-card-title class="font-weight-medium secondary--text primary py-3">
          <span>Record Settlement</span>
          <v-spacer />
          <v-btn icon x-small :disabled="saving" @click="settlementDialog = false">
            <v-icon x-small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <p class="si-settlement__hint">
            {{ money(selected.net_amount) }} invoiced for
            {{ date(selected.from_date) }} – {{ date(selected.to_date) }}.
          </p>
          <TransactionDetailsForm ref="detailsForm" v-model="transaction" />
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn text small :disabled="saving" @click="settlementDialog = false">
            Cancel
          </v-btn>
          <v-btn small depressed color="primary" :loading="saving" @click="saveSettlement">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import TransactionDetailsForm from "@/components/revenueSplit/TransactionDetailsForm.vue";

const emptyTransaction = () => ({
  transaction_id: "",
  transaction_date: "",
  utr_number: "",
  bank_account_number: "",
  bank_name: "",
  payment_mode: "",
  remarks: "",
});

export default {
  components: { TransactionDetailsForm },

  data() {
    return {
      partners: [],
      invoices: [],
      partnerId: null,
      search: "",
      loading: false,
      saving: false,
      detailDialog: false,
      settlementDialog: false,
      selected: null,
      transaction: emptyTransaction(),
      headers: [
        { text: "Period", value: "period", sortable: false },
        { text: "Invoice No", value: "invoice_number" },
        { text: "Collections", value: "collection_amount", align: "end" },
        { text: "Gross", value: "gross_amount", align: "end" },
        { text: "GST", value: "gst_amount", align: "end" },
        { text: "Net", value: "net_amount", align: "end" },
        { text: "Status", value: "status" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
    };
  },

  computed: {
    totals() {
      return this.invoices.reduce(
        (acc, invoice) => ({
          net: acc.net + Number(invoice.net_amount || 0),
          settled:
            acc.settled +
            (invoice.status === "settled" ? Number(invoice.net_amount || 0) : 0),
        }),
        { net: 0, settled: 0 },
      );
    },
  },

  created() {
    this.$store.commit("setBreadcrumbs", [
      { text: "Partner Revenue" },
      { text: "Service Invoices" },
    ]);
    this.getPartners();
  },

  methods: {
    async getPartners() {
      try {
        const res = await this.$axios.get("partners");
        this.partners = (res.data.data || []).filter(
          (partner) => partner.type !== "Sponsor",
        );
      } catch (err) {
        this.notifyError(err, "Unable to load partners");
      }
    },

    async loadInvoices() {
      if (!this.partnerId) return;

      this.loading = true;
      try {
        const res = await this.$axios.get("/revenue_service_invoices", {
          params: { partner_id: this.partnerId },
        });
        this.invoices = res.data.data || [];
      } catch (err) {
        this.invoices = [];
        this.notifyError(err, "Unable to load service invoices");
      } finally {
        this.loading = false;
      }
    },

    openInvoice(invoice) {
      this.selected = invoice;
      this.settlementDialog = false;
      this.detailDialog = true;
    },

    openSettlement(invoice) {
      this.selected = invoice;
      this.detailDialog = false;
      this.transaction = {
        ...emptyTransaction(),
        transaction_id: invoice.transaction_id || "",
        transaction_date: invoice.transaction_date || "",
        utr_number: invoice.utr_number || "",
        bank_account_number: invoice.bank_account_number || "",
        bank_name: invoice.bank_name || "",
        payment_mode: invoice.payment_mode || "",
        remarks: invoice.remarks || "",
      };
      this.settlementDialog = true;
      this.$nextTick(() => this.$refs.detailsForm?.reset());
    },

    async saveSettlement() {
      if (!this.$refs.detailsForm.validate()) {
        this.$store.commit("showSnackbar", {
          message: "Transaction details are required",
          color: "error",
        });
        return;
      }

      this.saving = true;
      try {
        await this.$axios.put(`/revenue_service_invoices/${this.selected.id}`, {
          transaction_details: this.transaction,
        });

        this.$store.commit("showSnackbar", {
          message: "Settlement recorded",
          color: "success",
        });

        this.settlementDialog = false;
        await this.loadInvoices();
      } catch (err) {
        this.notifyError(err, "Unable to record the settlement");
      } finally {
        this.saving = false;
      }
    },

    batchLabel(invoice) {
      if (!invoice.batch_year || !invoice.batch_no) return "All Batches";
      const month = invoice.batch_no === 2 ? "Jul" : "Jan";
      return `${month} ${String(invoice.batch_year).slice(-2)} Batch`;
    },

    statusColor(status) {
      return { settled: "success", raised: "primary" }[status] || "grey";
    },

    statusLabel(status) {
      return { settled: "Settled", raised: "Raised" }[status] || "Draft";
    },

    money(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    date(value) {
      if (!value) return "—";
      return new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    notifyError(err, fallback) {
      this.$store.commit("showSnackbar", {
        message: err?.response?.data?.message || fallback,
        color: "error",
      });
    },
  },
};
</script>

<style scoped>
.si-page {
  --si-line: #e4e7ec;
  --si-ink: #101828;
  --si-ink-2: #475467;
  --si-ink-3: #98a2b3;
  padding: 10px 14px 40px;
  margin: 0 15px;
}

.si-toolbar {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #fff;
  border-bottom: 1px solid var(--si-line);
  margin: -10px -14px 0;
}

.si-toolbar__inner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 14px;
}

.si-field {
  max-width: 230px;
  flex: 0 1 230px;
}

.si-search {
  max-width: 220px;
  flex: 0 1 220px;
}

.si-summary {
  display: flex;
  gap: 14px;
  font-size: 12.5px;
  color: var(--si-ink-2);
}

.si-summary__item strong {
  color: var(--si-ink);
  font-variant-numeric: tabular-nums;
}

.si-summary__item--muted strong {
  color: var(--si-ink-2);
}

.si-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
}

.si-state--card {
  border-radius: 8px;
  background: #fff;
  margin-top: 12px;
}

.si-state__title {
  margin: 14px 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--si-ink);
}

.si-state__text {
  margin: 0;
  font-size: 13px;
  color: var(--si-ink-2);
}

.si-card {
  margin-top: 12px;
  border-radius: 8px;
  background: #fff;
}

.si-table ::v-deep tbody tr {
  cursor: pointer;
}

.si-table ::v-deep th {
  white-space: nowrap;
}

.si-period {
  display: block;
  font-weight: 600;
  color: var(--si-ink);
}

.si-sub {
  display: block;
  font-size: 11px;
  color: var(--si-ink-3);
}

.si-meta {
  margin-bottom: 14px;
}

.si-meta__row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 3px 0;
  font-size: 12.5px;
  color: var(--si-ink-2);
}

.si-lines {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}

.si-lines th {
  padding: 6px 8px;
  text-align: left;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--si-ink-2);
  background: #f9fafb;
  border-bottom: 1px solid var(--si-line);
}

.si-lines td {
  padding: 6px 8px;
  border-bottom: 1px solid #f4f5f7;
  color: var(--si-ink-2);
}

.si-lines tfoot td {
  font-weight: 700;
  color: var(--si-ink);
  background: #f9fafb;
}

.si-num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.si-settlement {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid var(--si-line);
}

.si-settlement__title {
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--si-ink-2);
}

.si-settlement__hint {
  margin: 0 0 12px;
  font-size: 12.5px;
  color: var(--si-ink-2);
}

@media (max-width: 600px) {
  .si-page {
    margin: 0;
    padding: 10px 10px 40px;
  }

  .si-toolbar {
    position: static;
    margin: -10px -10px 0;
  }

  .si-field,
  .si-search {
    max-width: none;
    flex: 1 1 100%;
  }
}
</style>
